import { hydrateRoot, createRoot } from "react-dom/client";
import { Router } from "wouter";
import { QueryClient, QueryClientProvider, HydrationBoundary } from "@tanstack/react-query";
import { getQueryFn } from "./lib/queryClient";
import { preloadDesignWorkshopDetail } from "./lib/design-workshop-detail-loader";
import { preloadKidsDetail } from "./lib/kids-detail-loader";
import App from "./App";
import "./index.css";

const rootEl = document.getElementById("root")!;
const hasSSRContent = rootEl.innerHTML.trim().length > 0;

// Opt out of browser scroll restoration so SPA navigation always lands at
// scroll-top via our own handler in App.tsx. Default 'auto' restoration was
// causing users to land mid-page on routes they'd previously scrolled (most
// visible on tall pages like /events/:slug).
if (typeof history !== "undefined" && "scrollRestoration" in history) {
  history.scrollRestoration = "manual";
}

// Pick up server-dehydrated React Query state
const dehydratedState = (window as any).__REACT_QUERY_STATE__ || undefined;

// Create a client QueryClient (singleton for the browser session)
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60,
      retry: false,
    },
    mutations: { retry: false },
  },
});

const appTree = (
  <QueryClientProvider client={queryClient}>
    <HydrationBoundary state={dehydratedState}>
      <Router>
        <App queryClientOverride={queryClient} />
      </Router>
    </HydrationBoundary>
  </QueryClientProvider>
);

/**
 * The design-workshop detail page is code-split out of the entry chunk
 * (see lib/design-workshop-detail-loader.ts). On direct hits to that page the
 * server HTML contains the full design page, so the chunk must be resolved
 * BEFORE hydrateRoot — otherwise the client's first render wouldn't match the
 * server tree. Detect it from the dehydrated query state: the event-detail
 * query data is a single event object whose seriesSlug identifies the series.
 */
function ssrStateHasSeriesEvent(state: unknown, seriesSlug: string): boolean {
  try {
    const queries = (state as { queries?: Array<{ state?: { data?: unknown } }> })?.queries;
    if (!Array.isArray(queries)) return false;
    return queries.some((q) => {
      const data = q?.state?.data as { seriesSlug?: string } | undefined;
      return (
        !!data &&
        typeof data === "object" &&
        !Array.isArray(data) &&
        data.seriesSlug === seriesSlug
      );
    });
  } catch {
    return false;
  }
}

if (hasSSRContent) {
  const hydrate = () =>
    hydrateRoot(rootEl, appTree, {
      // React 19 SSR injects <link rel="preload"> inside the root div for images.
      // Browsers hoist these to <head>, causing a DOM mismatch on hydration.
      // Suppress these recoverable errors — they don't affect functionality.
      onRecoverableError(error) {
        if (typeof error === "object" && error && "message" in error) {
          const msg = (error as Error).message;
          if (msg.includes("418") || msg.includes("Hydration") || msg.includes("hydrat")) return;
        }
        console.error(error);
      },
    });

  if (ssrStateHasSeriesEvent(dehydratedState, "claude-for-design-workshop")) {
    // Hydrate as soon as the design-workshop chunk is in — and still hydrate
    // if it fails (the gate inside event-detail retries and shows a skeleton).
    preloadDesignWorkshopDetail().then(hydrate, hydrate);
  } else if (ssrStateHasSeriesEvent(dehydratedState, "claude-code-for-kids")) {
    // Same contract for the kids camp page (lib/kids-detail-loader.ts).
    preloadKidsDetail().then(hydrate, hydrate);
  } else {
    hydrate();
  }
} else {
  createRoot(rootEl).render(appTree);
}
